import { SEATS, cloneDeck, applyTrump, cardCode } from "./setbackRules";

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// The full set of still-unseen cards (deck minus my hand minus every card
// already played) - not yet shuffled or trump-flagged. Split out from
// determinizeHands so a caller comparing several candidates (e.g. chooseBid
// weighing multiple trump suits) can shuffle this once per iteration and
// reuse the identical card-to-seat assignment for every candidate, instead
// of each candidate drawing its own independent, uncorrelated shuffle.
const unseenPool = ({ myHand, playedCards }) => {
  const usedCodes = new Set([
    ...myHand.map(cardCode),
    ...playedCards.map(cardCode),
  ]);
  return cloneDeck().filter((card) => !usedCodes.has(cardCode(card)));
};

// Samples a full, consistent 4-hand deal from the point of view of `mySeat`:
// mySeat's own hand is exactly what it actually holds; the other three
// seats' hands are randomly reconstructed from the still-unseen cards,
// sized to how many cards each seat is publicly known to still be holding.
// This is the only hidden-information sampling the AI does - it never
// looks at another seat's real cards.
//
// `remainingPool`, when given, is an already-shuffled unseenPool() (see
// above) to slice up directly instead of shuffling a fresh one - this is
// what lets multiple calls share the same simulated opponent hands (common
// random numbers) rather than each drawing independently.
const determinizeHands = ({ mySeat, myHand, trump, playedCards, handSizes, remainingPool }) => {
  const pool = remainingPool || shuffle(unseenPool({ myHand, playedCards }));
  const remaining = pool.map((card) => applyTrump(card, trump));

  const hands = { [mySeat]: myHand.map((card) => applyTrump(card, trump)) };
  let idx = 0;
  for (const seat of SEATS) {
    if (seat === mySeat) continue;
    const size = handSizes[seat] ?? 0;
    hands[seat] = remaining
      .slice(idx, idx + size)
      .map((card) => ({ ...card, details: { ...card.details, owner: seat } }));
    idx += size;
  }
  return hands;
};

export { determinizeHands, shuffle, unseenPool };
